import { useState, useEffect } from "react";
import {
  WifiOff,
  RefreshCw,
  HardDrive,
  Clock,
  Trash2,
  FolderOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { pwaManager } from "@/utils/pwaUtils";
import { pwaCacheManager } from "@/utils/pwaCacheManager";
import { CachedContentBrowser } from "./CachedContentBrowser";
import { cn } from "@/lib/utils";

interface PWAOfflineScreenProps {
  onRetry: () => void;
}

export function PWAOfflineScreen({ onRetry }: PWAOfflineScreenProps) {
  const [storageInfo, setStorageInfo] = useState<{
    quota: number;
    usage: number;
  } | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [cacheStats, setCacheStats] = useState<any>(null);

  useEffect(() => {
    // Get storage information and cache stats
    const loadStorageInfo = async () => {
      const [info, stats] = await Promise.all([
        pwaManager.getStorageQuota(),
        pwaCacheManager.getCacheStats(),
      ]);
      setStorageInfo(info);
      setCacheStats(stats);
    };

    // Get last sync time from localStorage
    const lastSync = localStorage.getItem("lastSyncTime");
    if (lastSync) {
      const date = new Date(lastSync);
      setLastSyncTime(date.toLocaleString());
    }

    loadStorageInfo();
  }, []);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const clearCache = async () => {
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));

      // Also clear PWA cache manager
      await pwaCacheManager.clearPageCache();

      // Reload the page to refresh the service worker
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-primary rounded-full flex items-center justify-center text-3xl font-bold text-primary-foreground">
            P
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">PayStreem</h1>
          <p className="text-muted-foreground">You're currently offline</p>
        </div>

        {/* Offline Status Card */}
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="w-12 h-12 mx-auto mb-3 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <WifiOff className="w-6 h-6 text-yellow-400" />
            </div>
            <CardTitle className="text-xl">No Internet Connection</CardTitle>
            <CardDescription>
              Don't worry! You can still browse cached content and your app will
              sync when you're back online.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Retry Button */}
            <Button onClick={onRetry} className="w-full" variant="default">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>

            {/* Storage Information */}
            {storageInfo && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center text-muted-foreground">
                    <HardDrive className="w-4 h-4 mr-2" />
                    Storage Used
                  </span>
                  <span className="text-foreground font-medium">
                    {formatBytes(storageInfo.usage)} /{" "}
                    {formatBytes(storageInfo.quota)}
                  </span>
                </div>

                {/* Storage Progress Bar */}
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={cn(
                      "h-2 rounded-full transition-all duration-300",
                      storageInfo.usage / storageInfo.quota > 0.8
                        ? "bg-red-500"
                        : storageInfo.usage / storageInfo.quota > 0.6
                          ? "bg-yellow-500"
                          : "bg-primary",
                    )}
                    style={{
                      width: `${Math.min((storageInfo.usage / storageInfo.quota) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Last Sync Time */}
            {lastSyncTime && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Last synced
                </span>
                <span>{lastSyncTime}</span>
              </div>
            )}

            {/* Clear Cache Button */}
            <Button
              onClick={clearCache}
              variant="outline"
              size="sm"
              className="w-full mt-4"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Cache
            </Button>
          </CardContent>
        </Card>

        {/* Offline Content Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="content">
              <FolderOpen className="w-4 h-4 mr-2" />
              Cached Content
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Available Offline</CardTitle>
                <CardDescription>
                  Here's what you can still do while offline:
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center text-muted-foreground">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3" />
                    Browse cached profiles and tracks
                  </li>
                  <li className="flex items-center text-muted-foreground">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3" />
                    Play downloaded music
                  </li>
                  <li className="flex items-center text-muted-foreground">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3" />
                    View your favorites and playlists
                  </li>
                  <li className="flex items-center text-muted-foreground">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3" />
                    Navigate to {cacheStats?.pageCount || 0} cached pages
                  </li>
                  <li className="flex items-center text-muted-foreground">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3" />
                    New content will sync when online
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="content">
            <CachedContentBrowser />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
